import SourceDot from './SourceDot';
import { CoinGeckoMark } from './home/HomeModule';
import { openLanding } from '../utils/landing';
import { PLUSZ_PATH } from '../utils/pricing';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { fmtPrice, fmtMktCap, fmtPct } from '../utils/format';
import { bgColor, tileInk } from '../utils/heatmapInk';
import { isStablecoin } from '../utils/stablecoins';


const LEGEND = [
  { bg: '#7f1d1d', label: '< −5%' },
  { bg: '#b91c1c', label: '−3%'   },
  { bg: '#991b1b', label: '−1%'   },
  { bg: '#1e293b', label: '±1%'   },
  { bg: '#16a34a', label: '+1%'   },
  { bg: '#15803d', label: '+3%'   },
  { bg: '#166534', label: '> +5%' },
];

const FILTERS = [
  { key: 'all',     label: 'Mind'         },
  { key: 'winners', label: '🟢 Nyertesek' },
  { key: 'losers',  label: '🔴 Vesztesek' },
  { key: 'big',     label: '⚡ Nagy mozgás'},
];


function getTile(idx, isMobile) {
  if (isMobile) {
    if (idx === 0) return { colSpan: 3, rowSpan: 3, tier: 'large' };
    if (idx <= 3)  return { colSpan: 2, rowSpan: 2, tier: 'med'   };
    if (idx <= 11) return { colSpan: 1, rowSpan: 2, tier: 'sm'    };
    return { colSpan: 1, rowSpan: 1, tier: 'xs' };
  }
  if (idx === 0) return { colSpan: 4, rowSpan: 3, tier: 'large' };
  if (idx === 1) return { colSpan: 3, rowSpan: 3, tier: 'large' };
  if (idx <= 7)  return { colSpan: 2, rowSpan: 2, tier: 'med'   };
  if (idx <= 17) return { colSpan: 1, rowSpan: 2, tier: 'sm'    };
  return { colSpan: 1, rowSpan: 1, tier: 'xs' };
}

function TooltipCard({ coin, x, y, timeframe }) {
  const pct24h = coin.price_change_percentage_24h ?? 0;
  const pct7d  = coin.price_change_percentage_7d_in_currency ?? 0;
  const active = timeframe === '24h' ? pct24h : pct7d;
  const borderColor = active >= 1
    ? 'rgba(34,197,94,0.55)'
    : active <= -1
      ? 'rgba(239,68,68,0.55)'
      : 'rgba(100,116,139,0.4)';

  const tipW = 210, tipH = 185;
  const left = Math.min(x + 18, window.innerWidth  - tipW - 10);
  const top  = Math.max(10, Math.min(y - 60, window.innerHeight - tipH - 10));

  return (
    <div className="hm-tooltip" style={{ left, top, borderColor }}>
      <div className="hm-tt-header">
        <img src={coin.image} alt={coin.symbol} className="hm-tt-icon" />
        <div>
          <div className="hm-tt-name">{coin.name}</div>
          <div className="hm-tt-sym">{coin.symbol.toUpperCase()} · #{coin.market_cap_rank}</div>
        </div>
      </div>
      <div className="hm-tt-price">{fmtPrice(coin.current_price)}</div>
      <div className="hm-tt-row">
        <span className="hm-tt-key">24h</span>
        <span className={`hm-tt-val ${pct24h >= 0 ? 'positive' : 'negative'}`}>{fmtPct(pct24h)}</span>
      </div>
      <div className="hm-tt-row">
        <span className="hm-tt-key">7d</span>
        <span className={`hm-tt-val ${pct7d >= 0 ? 'positive' : 'negative'}`}>{fmtPct(pct7d)}</span>
      </div>
      <div className="hm-tt-row">
        <span className="hm-tt-key">Piaci kap.</span>
        <span className="hm-tt-val">{fmtMktCap(coin.market_cap)}</span>
      </div>
      <div className="hm-tt-row">
        <span className="hm-tt-key">Forgalom</span>
        <span className="hm-tt-val">{fmtMktCap(coin.total_volume)}</span>
      </div>
      <div className="hm-tt-footer">⚡ Kattints a kiválasztáshoz</div>
    </div>
  );
}

const FREE_LIMIT = 50;

export default function HeatmapTab({ data: allData, loading, error, lastUpdated, onRefresh, onSelectCoin, watchlistCoins, meta, isFree = false, user, access }) {
  // FREE: the top 50 by market cap (reverse trial); PRO/trial: everything loaded (top 200).
  const data = useMemo(() => {
    if (!allData || !isFree) return allData;
    return [...allData].sort((a, b) => (a.market_cap_rank ?? 1e9) - (b.market_cap_rank ?? 1e9)).slice(0, FREE_LIMIT);
  }, [allData, isFree]);
  const hiddenCount = isFree && allData ? Math.max(0, allData.length - FREE_LIMIT) : 0;

  const [timeframe, setTimeframe] = useState('24h');
  const [filter,    setFilter]    = useState('all');
  const [tooltip,   setTooltip]   = useState(null);
  const [isMobile,  setIsMobile]  = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const coins = useMemo(() => {
    if (!data) return [];
    const base = data.filter(c => !isStablecoin(c));
    if (filter === 'all') return base;
    return base.filter(coin => {
      const pct = timeframe === '24h'
        ? (coin.price_change_percentage_24h ?? 0)
        : (coin.price_change_percentage_7d_in_currency ?? 0);
      if (filter === 'winners') return pct > 1;
      if (filter === 'losers')  return pct < -1;
      if (filter === 'big')     return Math.abs(pct) > 3;
      return true;
    });
  }, [data, timeframe, filter]);

  const statsBar = useMemo(() => {
    if (!data) return { winners: 0, losers: 0, neutral: 0, avg: 0 };
    const base = data.filter(c => !isStablecoin(c));
    const changes = base.map(c =>
      timeframe === '24h'
        ? (c.price_change_percentage_24h ?? 0)
        : (c.price_change_percentage_7d_in_currency ?? 0)
    );
    const winners = changes.filter(p => p > 1).length;
    const losers  = changes.filter(p => p < -1).length;
    const neutral = changes.filter(p => Math.abs(p) <= 1).length;
    const avg     = changes.length ? changes.reduce((s, p) => s + p, 0) / changes.length : 0;
    return { winners, losers, neutral, avg };
  }, [data, timeframe]);

  const handleSelect = useCallback((coin) => {
    const match = watchlistCoins?.find(c => c.id === coin.id) ?? coin;
    onSelectCoin?.(match);
  }, [watchlistCoins, onSelectCoin]);

  if (loading || (!data && !error)) {
    return (
      <div className="hm-state">
        <div className="hm-spinner">◈</div>
        <div>{loading ? 'Piaci adatok lekérése…' : 'Inicializálás…'}</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="hm-state hm-state-error">
        <span>⚠ {error}</span>
        <button className="scan-refresh-btn" onClick={onRefresh}>Újrapróbálom</button>
      </div>
    );
  }

  return (
    <div className="heatmap-tab">

      {/* ── Toolbar ── */}
      <div className="hm-toolbar">
        <div className="hm-toolbar-left">
          <span className="hm-title">🔥 Kripto Hőtérkép</span>
          <SourceDot source="CoinGecko" meta={meta ?? (lastUpdated ? { at: +lastUpdated } : null)} />
        </div>
        <div className="hm-toolbar-right">
          <div className="hm-tf-toggle">
            <button
              className={`hm-tf-btn${timeframe === '24h' ? ' active' : ''}`}
              onClick={() => setTimeframe('24h')}
            >24h</button>
            <button
              className={`hm-tf-btn${timeframe === '7d' ? ' active' : ''}`}
              onClick={() => setTimeframe('7d')}
            >7d</button>
          </div>
          <button
            className={`hm-refresh-btn${loading ? ' hm-refresh-spinning' : ''}`}
            onClick={onRefresh}
            title="Frissítés"
          >↻</button>
        </div>
      </div>

      {/* ── Legend ── */}
      <div className="hm-legend">
        {LEGEND.map(l => (
          <div key={l.label} className="hm-legend-item">
            <div className="hm-legend-swatch" style={{ background: l.bg }} />
            <span className="hm-legend-label">{l.label}</span>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="hm-filters">
        {FILTERS.map(f => (
          <button
            key={f.key}
            className={`hm-filter-btn hm-filter-btn--${f.key}${filter === f.key ? ' active' : ''}`}
            onClick={() => setFilter(f.key)}
          >{f.label}</button>
        ))}
        <span className="hm-filter-count">{coins.length} coin</span>
      </div>

      {/* ── Treemap grid ── */}
      <div className="hm-grid-wrap">
        <div
          className="tm-grid"
          style={{ gridTemplateColumns: `repeat(${isMobile ? 6 : 12}, 1fr)` }}
        >
          {coins.map((coin, idx) => {
            const pct = timeframe === '24h'
              ? (coin.price_change_percentage_24h ?? 0)
              : (coin.price_change_percentage_7d_in_currency ?? 0);
            const { colSpan, rowSpan, tier } = getTile(idx, isMobile);
            const isLarge  = tier === 'large';
            const isMed    = tier === 'med';
            const showLogo = isMed || isLarge;
            const pctStr   = fmtPct(pct, 2);
            const ink      = tileInk(pct);   // a felirat színe a háttérhez (kontraszt ≥ 4,5:1, félkövérnél ≥ 3:1)

            return (
              <div
                key={coin.id}
                className={`tm-tile tm-tile-${tier}`}
                style={{
                  gridColumn: `span ${colSpan}`,
                  gridRow:    `span ${rowSpan}`,
                  background: bgColor(pct),
                }}
                onMouseEnter={e => setTooltip({ coin, x: e.clientX, y: e.clientY })}
                onMouseMove={e  => setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : t)}
                onMouseLeave={() => setTooltip(null)}
                onClick={() => handleSelect(coin)}
              >
                {isLarge && showLogo && coin.image && (
                  <img src={coin.image} alt={coin.symbol} className="tm-logo-lg" draggable={false} />
                )}
                {!isLarge && isMed && coin.image && (
                  <img src={coin.image} alt={coin.symbol} className="tm-logo" draggable={false} />
                )}
                <div className="tm-body">
                  {isLarge && <span className="tm-name" style={{ color: ink.name }}>{coin.name}</span>}
                  <span className="tm-sym" style={{ color: ink.sym }}>{coin.symbol.toUpperCase()}</span>
                  <span className="tm-pct" style={{ color: ink.pct }}>{pctStr}</span>
                  {isLarge && <span className="tm-price" style={{ color: ink.price }}>{fmtPrice(coin.current_price)}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {isFree && hiddenCount > 0 && (
        <div className="pro-row">
          <span>További {hiddenCount} coin a Pluszban (top {FREE_LIMIT + hiddenCount}).</span>
          <button
            className="pro-row-cta"
            onClick={() => openLanding(PLUSZ_PATH, {
              user,
              event: 'plusz_cta_click',
              eventProps: { hely: 'terminal_hoterkep' },
            })}
          >
            Plusszal nyílik →
          </button>
        </div>
      )}

      {/* ── Bottom stats bar ── */}
      <div className="hm-stats-bar">
        <span className="hm-stats-win">🟢 {statsBar.winners} nyertes</span>
        <span className="hm-stats-sep">|</span>
        <span className="hm-stats-loss">🔴 {statsBar.losers} vesztes</span>
        <span className="hm-stats-sep">|</span>
        <span>⚪ {statsBar.neutral} semleges</span>
        <span className="hm-stats-sep">|</span>
        <span>
          Átl. változás:&nbsp;
          <strong className={statsBar.avg >= 0 ? 'positive' : 'negative'}>
            {fmtPct(statsBar.avg, 2)}
          </strong>
        </span>
        <span className="hm-stats-attr"><CoinGeckoMark /></span>
      </div>

      {/* ── Hover tooltip ── */}
      {tooltip && (
        <TooltipCard
          coin={tooltip.coin}
          x={tooltip.x}
          y={tooltip.y}
          timeframe={timeframe}
        />
      )}
    </div>
  );
}
